import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../../components/Buttons";
import { toast } from "react-toastify";
import { Request } from "../../hooks/Request";

import {
  DEFAULT_INSURANCE_MATERIAL_TYPE,
  INSURANCE_MATERIAL_RULES,
  getInsuranceMaterialTypeByImportType,
  getInsuranceRule,
} from "../../utils/insuranceRules";

function parsePreviewNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let normalized = String(value).replace(/\s/g, "").trim();
  if (!normalized || normalized === "-") return null;

  const numericMatch = normalized.match(/-?\d[\d.,]*/);
  if (!numericMatch) return null;

  normalized = numericMatch[0];
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma && hasDot) {
    normalized =
      normalized.lastIndexOf(",") > normalized.lastIndexOf(".")
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (hasComma) {
    normalized = /^-?\d{1,3}(,\d{3})+$/.test(normalized)
      ? normalized.replace(/,/g, "")
      : normalized.replace(",", ".");
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, "");
  }

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function isPreviewNumber(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (value === null || value === undefined || value === "") return false;

  const normalized = String(value).replace(/\s/g, "").trim();
  return /^-?(?:\d+|\d{1,3}(?:[,.]\d{3})+)(?:[,.]\d+)?$/.test(normalized);
}

function formatPreviewNumber(value) {
  const number = parsePreviewNumber(value);
  if (number === null) return value ?? "";

  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const NumericPreviewInput = ({ value, onChange, className }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!isFocused) {
      setDraft(formatPreviewNumber(value));
    }
  }, [isFocused, value]);

  return (
    <input
      className={className}
      type="text"
      inputMode="decimal"
      value={isFocused ? draft : formatPreviewNumber(value)}
      onFocus={() => {
        setIsFocused(true);
        setDraft(value === null || value === undefined ? "" : String(value));
      }}
      onChange={(e) => {
        const rawValue = e.target.value;
        setDraft(rawValue);

        if (rawValue.trim() === "") {
          onChange("");
          return;
        }

        const parsedValue = parsePreviewNumber(rawValue);
        if (parsedValue !== null) {
          onChange(parsedValue);
        }
      }}
      onBlur={() => setIsFocused(false)}
    />
  );
};

export const OrderPreviewModal = ({
  extractedData,
  onConfirm,
  onClose,
  title = "Previsualización de Orden de Compra",
  confirmText = "Confirmar y Guardar",
}) => {
  const [cabecera, setCabecera] = useState(extractedData.cabecera);
  const [items, setItems] = useState(extractedData.items);
  const [cargoRows, setCargoRows] = useState(extractedData.cargoRows ?? []);
  const [loading, setLoading] = useState(false);

  const procedureId = extractedData.procedureId;

  const { data: procData } = Request({
    urlApi: procedureId
      ? `/Procedure/${procedureId}/dataValues?fieldnames=tipoDeImportacion`
      : null,
  });

  const importTypeName = procData?.tipoDeImportacion || "";

  const resolvedRuleKey = useMemo(() => {
    if (!importTypeName) {
      const rule =
        INSURANCE_MATERIAL_RULES[extractedData.insuranceMaterialType];
      if (rule) return rule.value;
      return DEFAULT_INSURANCE_MATERIAL_TYPE;
    }
    return getInsuranceMaterialTypeByImportType(importTypeName);
  }, [importTypeName, extractedData.insuranceMaterialType]);

  const insuranceRule = getInsuranceRule(resolvedRuleKey);
  const purchaseOrderSubtotal = cabecera.find(
    (field) => field.name === "subtotalOrdenDeCompra",
  )?.Value;
  const showPurchaseOrderSubtotal =
    purchaseOrderSubtotal !== null &&
    purchaseOrderSubtotal !== undefined &&
    purchaseOrderSubtotal !== "";
  const purchaseOrderTotal = cabecera.find(
    (field) => field.name === "totalOrdenDeCompra",
  )?.Value;
  const showPurchaseOrderTotal =
    purchaseOrderTotal !== null &&
    purchaseOrderTotal !== undefined &&
    purchaseOrderTotal !== "";
  const exchangeRateInfo = extractedData.exchangeRateInfo;
  const showExchangeRateInfo = exchangeRateInfo?.currency === "EUR";

  const handleCabeceraChange = (index, value) => {
    const newC = [...cabecera];
    newC[index].Value = value;
    setCabecera(newC);
  };

  const handleItemChange = (index, key, value) => {
    const newItems = [...items];
    newItems[index][key] = value;
    setItems(newItems);
  };

  const handleCargoChange = (index, key, value) => {
    const updated = [...cargoRows];
    updated[index][key] = value;
    setCargoRows(updated);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await onConfirm({
        cabecera,
        items,
        cargoRows,
        procedureId: extractedData.procedureId,
        fieldsList: extractedData.fieldsList,
        insuranceMaterialType: resolvedRuleKey,
      });
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Error al confirmar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-w-[300px] max-w-5xl p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto relative animate-fade-in">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">
        {title}
      </h2>

      {showExchangeRateInfo && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <h3 className="text-lg font-bold mb-2">Tipo de cambio usado</h3>
          <p className="text-sm">
            Moneda: <span className="font-semibold">EUR</span>
          </p>
          <p className="text-sm">
            TC Bs/EUR: <span className="font-semibold">{exchangeRateInfo.exchangeRate}</span>
          </p>
          <p className="text-sm mt-2">
            {exchangeRateInfo.convertedFromBolivianos
              ? `La Orden de Compra fue convertida de Bs a EUR usando TC Bs/EUR: ${exchangeRateInfo.exchangeRate}.`
              : "La Orden de Compra fue importada directamente en EUR. No se aplicó conversión."}
          </p>
        </div>
      )}

      <h3 className="font-semibold text-xl text-blue-700 pb-2 mb-4 border-b border-blue-100">
        Datos Generales
      </h3>

      <div className="mb-6 p-2">
        <h3 className="font-semibold text-xl text-blue-700 pb-2 mb-4 border-b border-blue-100 mt-8">
          Clasificación para seguro
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="flex flex-col">
            <label className="text-normal font-semibold text-gray-600 mb-1">
              Tipo de importacion
            </label>
            <div className="p-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-semibold text-gray-800">
              {importTypeName || insuranceRule.label}
            </div>
          </div>

          <div className="rounded-md bg-white border border-blue-100 p-3">
            <p className="text-xs text-gray-500">Factor aplicado</p>
            <p className="text-lg font-bold text-blue-700">
              {insuranceRule.factor.toFixed(2)}
            </p>
          </div>

          <div className="rounded-md bg-white border border-blue-100 p-3">
            <p className="text-xs text-gray-500">Tasa sugerida</p>
            <p className="text-lg font-bold text-blue-700">
              {insuranceRule.ratePercent}%
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-600 mt-3">
          Esta clasificación se usará para calcular el Costo Total de la
          aplicación de seguro.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {cabecera.map((field, idx) => (
          <div
            key={idx}
            className="flex flex-col bg-gray-50 p-2 rounded border border-gray-100"
          >
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">
              {field.label}
            </label>
            {isPreviewNumber(field.Value) ? (
              <NumericPreviewInput
                className="p-1.5 border-b border-gray-300 bg-transparent focus:border-blue-500 outline-none text-gray-800 w-full"
                value={field.Value}
                onChange={(value) => handleCabeceraChange(idx, value)}
              />
            ) : (
              <input
                type="text"
                className="p-1.5 border-b border-gray-300 bg-transparent focus:border-blue-500 outline-none text-gray-800 w-full"
                value={field.Value}
                onChange={(e) => handleCabeceraChange(idx, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      <h3 className="font-semibold text-xl text-blue-700 pb-2 mb-4 border-b border-blue-100 mt-8">
        Productos / Ítems ({items.length})
      </h3>
      {items.length === 0 ? (
        <div className="p-4 bg-yellow-50 text-yellow-700 text-center rounded">
          No se detectaron ítems en el rango procesado.
        </div>
      ) : (
        <div className="overflow-x-auto shadow-sm ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-16"
                >
                  Nro
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Descripción
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24"
                >
                  Unidad
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24"
                >
                  Cant.
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24"
                >
                  Descuento
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32"
                >
                  Fecha Entrega
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-28"
                >
                  P. Unitario
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-28"
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <input
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.itemNo}
                      onChange={(e) =>
                        handleItemChange(idx, "itemNo", e.target.value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <input
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.desc}
                      onChange={(e) =>
                        handleItemChange(idx, "desc", e.target.value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <input
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.unit}
                      onChange={(e) =>
                        handleItemChange(idx, "unit", e.target.value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <NumericPreviewInput
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.amount}
                      onChange={(value) =>
                        handleItemChange(idx, "amount", value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <NumericPreviewInput
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.discount}
                      onChange={(value) =>
                        handleItemChange(idx, "discount", value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <input
                      className="w-full border border-gray-200 rounded p-1"
                      type="date"
                      value={item.delivery}
                      onChange={(e) =>
                        handleItemChange(idx, "delivery", e.target.value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <NumericPreviewInput
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.unitPrice}
                      onChange={(value) =>
                        handleItemChange(idx, "unitPrice", value)
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <NumericPreviewInput
                      className="w-full border border-gray-200 rounded p-1"
                      value={item.itemTotal}
                      onChange={(value) =>
                        handleItemChange(idx, "itemTotal", value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {showPurchaseOrderSubtotal && (
            <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-600">
                  Subtotal orden de compra
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {formatPreviewNumber(purchaseOrderSubtotal)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <h3 className="font-semibold text-xl text-blue-700 pb-2 mb-4 border-b border-blue-100 mt-8">
        Cargos Adicionales ({cargoRows.length})
      </h3>
      {cargoRows.length > 0 ? (
        <div className="overflow-x-auto shadow-sm ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Concepto
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                  Monto
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {cargoRows.map((cargo, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm">
                    <input
                      className="w-full border border-gray-200 rounded p-1"
                      value={cargo.label}
                      onChange={(e) =>
                        handleCargoChange(idx, "label", e.target.value)
                      }
                    />
                  </td>
                  <td className="px-3 py-3 text-sm">
                    <NumericPreviewInput
                      className="w-full border border-gray-200 rounded p-1"
                      value={cargo.value}
                      onChange={(value) =>
                        handleCargoChange(idx, "value", value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
          No se detectaron cargos adicionales.
        </div>
      )}
      {showPurchaseOrderTotal && (
        <div className="mt-3 flex justify-end rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">
              Total orden de compra
            </p>
            <p className="text-2xl font-bold text-blue-900">
              {formatPreviewNumber(purchaseOrderTotal)}
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-end gap-4 p-4 bg-gray-50 border-t border-gray-200 -mx-6 -mb-6 rounded-b-lg">
        <Button
          type="button"
          onClick={onClose}
          className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 py-2.5 px-6"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={submit}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 shadow"
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i> Procesando...
            </>
          ) : (
            <>
              <i className="fas fa-check mr-2"></i> {confirmText}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
